import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// System-state anomalies only. Official whispers come exclusively from the
// admin scheduler (`official` prop) — never AI-generated fallbacks.
const SYSTEM_STATES = ["SIGNAL DETECTED", "TRANSMISSION INCOMPLETE", "SIGNAL UNRESOLVED"];

const INTENSITY = {
  subtle: { opacity: 0.3, chance: 0.45, minWait: 18000, spread: 34000 },
  standard: { opacity: 0.45, chance: 0.6, minWait: 12000, spread: 24000 },
  strong: { opacity: 0.6, chance: 0.8, minWait: 8000, spread: 16000 },
};

export default function Whisper({ config, id, official = [] }) {
  const enabled = !!config?.enabled;
  const hasOfficial = official.length > 0;
  const pool = hasOfficial
    ? official.map((w) => ({ phrase: w.phrase, intensity: w.intensity || "subtle" }))
    : SYSTEM_STATES.map((p) => ({ phrase: p, intensity: "subtle" }));
  const mode = INTENSITY[pool[0].intensity] || INTENSITY.subtle;
  const [shown, setShown] = useState(null);
  const timer = useRef(null);
  const poolRef = useRef(pool);
  poolRef.current = pool;

  useEffect(() => {
    if (!enabled) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let alive = true;
    const schedule = () => {
      const wait = mode.minWait + Math.random() * mode.spread;
      timer.current = setTimeout(() => {
        if (!alive) return;
        if (Math.random() < mode.chance) {
          const pick = poolRef.current[Math.floor(Math.random() * poolRef.current.length)];
          setShown(pick);
          setTimeout(() => alive && setShown(null), 5200);
        }
        schedule();
      }, wait);
    };
    schedule();
    return () => {
      alive = false;
      clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hasOfficial]);

  if (!enabled) return null;
  return (
    <div className="absolute bottom-5 right-6 md:right-10 pointer-events-none select-none" data-testid={`whisper-${id}`}>
      <AnimatePresence>
        {shown && (
          <motion.span
            key={shown.phrase}
            initial={{ opacity: 0 }}
            animate={{ opacity: (INTENSITY[shown.intensity] || INTENSITY.subtle).opacity }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.4, ease: "easeInOut" }}
            className="font-mono text-[9px] tracking-[0.45em] text-white"
            data-testid={`whisper-${id}-phrase`}
          >
            {shown.phrase}
          </motion.span>
        )}
      </AnimatePresence>
      {!shown && (
        <span className="font-mono text-[9px] tracking-[0.45em] text-white opacity-0 group-hover:opacity-20 transition-opacity duration-[2500ms]">
          {pool[0].phrase}
        </span>
      )}
    </div>
  );
}
